import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import ca from './locales/ca.json';
import es from './locales/es.json';
import en from './locales/en.json';

export const languages = ['ca', 'es', 'en'] as const;
export type Language = (typeof languages)[number];
export type Translation = typeof ca;

const translations: Record<Language, Translation> = { ca, es, en };
export const languageLabels: Record<Language, string> = {
  ca: ca.languageName,
  es: es.languageName,
  en: en.languageName,
};
const projectPageSlugs: Record<Language, string> = {
  ca: 'projecte',
  es: 'proyecto',
  en: 'project',
};

interface LocalizationContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
  copy: Translation;
  locale: string;
  projectPageSlug: string;
  projectPagePath: string;
}

const LocalizationContext = createContext<LocalizationContextValue | null>(null);

const getInitialLanguage = (): Language => {
  const browserLanguages = navigator.languages.length
    ? navigator.languages
    : [navigator.language];
  const supportedLanguage = browserLanguages
    .map((language) => language.toLowerCase().split('-')[0])
    .find((language): language is Language => languages.includes(language as Language));

  return supportedLanguage ?? 'ca';
};

interface LocalizationProviderProps extends React.PropsWithChildren {
  initialLanguage?: Language;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  initialLanguage,
}) => {
  const [language, setLanguage] = useState<Language>(() => initialLanguage ?? getInitialLanguage());

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      copy: translations[language],
      locale: language === 'ca' ? 'ca-ES' : language === 'es' ? 'es-ES' : 'en-GB',
      projectPageSlug: projectPageSlugs[language],
      projectPagePath: `/${projectPageSlugs[language]}`,
    }),
    [language]
  );

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = (): LocalizationContextValue => {
  const context = useContext(LocalizationContext);
  if (!context) throw new Error('useLocalization must be used within LocalizationProvider');
  return context;
};
